import { useState } from 'react';
import type { GitFileStatus, GitBranch } from '../api/types.js';
import { useWorkspaceGitStatus, useWorkspaceGitBranches } from '../hooks/use-workspace.js';

interface GitPanelProps {
  serverId: string;
  currentPath?: string;
  onRefresh: () => void;
}

const STATUS_INDICATORS: Record<GitFileStatus['status'], { label: string; color: string; bg: string }> = {
  modified:  { label: 'M', color: 'var(--color-warn)',    bg: 'var(--color-warn-dim)' },
  added:     { label: 'A', color: 'var(--color-success)', bg: 'var(--color-success-dim)' },
  staged:    { label: 'A', color: 'var(--color-success)', bg: 'var(--color-success-dim)' },
  deleted:   { label: 'D', color: 'var(--color-error)',   bg: 'var(--color-error-dim)' },
  renamed:   { label: 'R', color: 'var(--color-brand)',   bg: 'var(--color-brand-dim)' },
  untracked: { label: '?', color: 'var(--color-text-3)',  bg: 'var(--color-surface-3)' },
};

function groupFiles(files: GitFileStatus[]): {
  staged: GitFileStatus[];
  modified: GitFileStatus[];
  untracked: GitFileStatus[];
} {
  const staged: GitFileStatus[] = [];
  const modified: GitFileStatus[] = [];
  const untracked: GitFileStatus[] = [];

  for (const file of files) {
    if (file.staged) staged.push(file);
    else if (file.status === 'untracked') untracked.push(file);
    else modified.push(file);
  }

  return { staged, modified, untracked };
}

function FileStatusEntry({ file }: { file: GitFileStatus }) {
  const indicator = STATUS_INDICATORS[file.status] ?? STATUS_INDICATORS.modified;
  return (
    <div className="flex items-center gap-2 px-3 py-1 text-xs">
      <span
        className="inline-flex h-4 w-4 items-center justify-center rounded text-[10px] font-bold"
        style={{ color: indicator.color, backgroundColor: indicator.bg }}
      >
        {indicator.label}
      </span>
      <span className="truncate font-mono" style={{ color: 'var(--color-text-2)' }} title={file.path}>
        {file.path}
      </span>
    </div>
  );
}

function FileGroup({ title, files }: { title: string; files: GitFileStatus[] }) {
  if (files.length === 0) return null;
  return (
    <div>
      <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide" style={{ color: 'var(--color-text-3)' }}>
        {title} ({files.length})
      </div>
      {files.map((file) => (
        <FileStatusEntry key={file.path} file={file} />
      ))}
    </div>
  );
}

function BranchDropdown({ branches }: { branches: GitBranch[] }) {
  const [isOpen, setIsOpen] = useState(false);
  const local = branches.filter((b) => !b.remote);
  const remote = branches.filter((b) => b.remote);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1 rounded px-1.5 py-0.5 text-xs"
        style={{ color: 'var(--color-text-3)' }}
        aria-label="Show branches"
      >
        <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
        </svg>
        {branches.length} branches
      </button>
      {isOpen && (
        <div
          className="absolute left-0 top-full z-10 mt-1 max-h-48 w-56 overflow-y-auto rounded-lg py-1 shadow-lg"
          style={{ border: '1px solid var(--color-border)', backgroundColor: 'var(--color-surface)' }}
          data-testid="branch-dropdown"
        >
          {local.length > 0 && (
            <>
              <div className="px-3 py-1 text-[10px] font-semibold uppercase" style={{ color: 'var(--color-text-3)' }}>Local</div>
              {local.map((b) => (
                <div key={b.name} className="flex items-center gap-2 px-3 py-1 text-xs" style={{ color: 'var(--color-text-2)' }}>
                  {b.current && <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: 'var(--color-success)' }} />}
                  <span className={`truncate ${b.current ? 'font-medium' : ''}`}>{b.name}</span>
                </div>
              ))}
            </>
          )}
          {remote.length > 0 && (
            <>
              <div className="px-3 py-1 text-[10px] font-semibold uppercase" style={{ color: 'var(--color-text-3)' }}>Remote</div>
              {remote.map((b) => (
                <div key={b.name} className="truncate px-3 py-1 text-xs" style={{ color: 'var(--color-text-3)' }}>
                  {b.name}
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export function GitPanel({ serverId, currentPath, onRefresh }: GitPanelProps) {
  const { data: gitStatus, isLoading: statusLoading, isError: statusError } = useWorkspaceGitStatus(serverId, currentPath);
  const { data: gitBranches } = useWorkspaceGitBranches(serverId, currentPath);
  const [isCollapsed, setIsCollapsed] = useState(false);

  const isNotGitRepo = statusError;
  const groups = gitStatus ? groupFiles(gitStatus.files) : null;

  return (
    <div className="flex flex-col" style={{ borderTop: '1px solid var(--color-border)' }} data-testid="git-panel">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2" style={{ borderBottom: '1px solid var(--color-border)' }}>
        <button
          type="button"
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide"
          style={{ color: 'var(--color-text-3)' }}
        >
          <svg
            className={`h-3 w-3 transition-transform ${isCollapsed ? '' : 'rotate-90'}`}
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
          </svg>
          Git
        </button>
        <button
          type="button"
          onClick={onRefresh}
          className="rounded p-1"
          style={{ color: 'var(--color-text-3)' }}
          title="Refresh git status"
          aria-label="Refresh git status"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.992 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182"
            />
          </svg>
        </button>
      </div>

      {!isCollapsed && (
        <div className="overflow-y-auto">
          {!currentPath && (
            <div className="px-3 py-4 text-center text-xs" style={{ color: 'var(--color-text-3)' }}>
              Select a file to see git status
            </div>
          )}

          {currentPath && statusLoading && (
            <div className="space-y-1 px-3 py-2">
              <div className="h-4 w-20 animate-pulse rounded" style={{ backgroundColor: 'var(--color-surface-3)' }} />
              <div className="h-4 w-32 animate-pulse rounded" style={{ backgroundColor: 'var(--color-surface-3)' }} />
            </div>
          )}

          {currentPath && isNotGitRepo && (
            <div className="px-3 py-4 text-center text-xs" style={{ color: 'var(--color-text-3)' }}>
              Not a git repository
            </div>
          )}

          {gitStatus && (
            <div>
              {/* Branch badge */}
              <div className="flex items-center gap-2 px-3 py-2">
                <svg className="h-4 w-4" style={{ color: 'var(--color-text-3)' }} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 15.75L3 12m0 0l3.75-3.75M3 12h18" />
                </svg>
                <span
                  className="rounded-full px-2 py-0.5 text-xs font-medium"
                  style={{ backgroundColor: 'var(--color-brand-dim)', color: 'var(--color-brand)' }}
                  data-testid="branch-badge"
                >
                  {gitStatus.branch}
                </span>
                {gitBranches && <BranchDropdown branches={gitBranches.branches} />}
              </div>

              {/* File groups */}
              {groups && (
                <div className="space-y-1 pb-2">
                  <FileGroup title="Staged" files={groups.staged} />
                  <FileGroup title="Modified" files={groups.modified} />
                  <FileGroup title="Untracked" files={groups.untracked} />
                  {groups.staged.length === 0 && groups.modified.length === 0 && groups.untracked.length === 0 && (
                    <div className="px-3 py-2 text-xs" style={{ color: 'var(--color-text-3)' }}>Working tree clean</div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
